import { PasskeyRegistrationVerifyRequestSchema } from "@oyako-flow/contracts";
import type { components } from "../../../clients/typescript/src/generated";
import {
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
} from "@simplewebauthn/browser";
import { useState } from "react";
import { createOyakoFlowClient } from "../../../clients/typescript/src/index";

export function NativeConsent({
  requestId,
  workspaceId,
  csrf,
  requiresPasskey = false,
  onLogout,
}: {
  requestId: string;
  workspaceId: string;
  csrf: string;
  requiresPasskey?: boolean;
  onLogout: () => void;
}) {
  const [error, setError] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [added, setAdded] = useState(
      () => sessionStorage.getItem(`oyako-passkey-added:${requestId}`) === "true",
    );
  const [label, setLabel] = useState("保護者のPasskey");
  const verifying = new URLSearchParams(location.search).get("native_action") === "reauthenticate";
  const client = createOyakoFlowClient("");
  const headers = () => ({
    "X-Workspace-Id": workspaceId,
    "X-CSRF-Token": csrf,
    "Idempotency-Key": crypto.randomUUID(),
  });
  async function addPasskey() {
    setBusy(true);
    setError(null);
    try {
      const options = await client.POST("/api/v1/workspaces/{workspaceId}/passkeys/add/options", {
        params: { path: { workspaceId }, header: headers() },
        body: { device_label: label },
      });
      if (!options.data)
        throw new Error("登録を開始できませんでした。もう一度ログインしてください。");
      const credential = await startRegistration({
        optionsJSON: options.data.public_key as unknown as PublicKeyCredentialCreationOptionsJSON,
      });
      const result = await client.POST("/api/v1/workspaces/{workspaceId}/passkeys/add/verify", {
        params: { path: { workspaceId }, header: headers() },
        body: PasskeyRegistrationVerifyRequestSchema.parse({
          ceremony_id: options.data.ceremony_id,
          credential,
        }) as components["schemas"]["PasskeyRegistrationVerifyRequest"],
      });
      if (!result.data)
        throw new Error("Passkeyを登録できませんでした。登録をやり直してください。");
      sessionStorage.setItem(`oyako-passkey-added:${requestId}`, "true");
      setAdded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "登録できませんでした。");
    } finally {
      setBusy(false);
    }
  }
  async function approve() {
    setBusy(true);
    setError(null);
    try {
      const result = await client.POST("/api/v1/native/authorizations/approve", {
        params: { header: headers() },
        body: { request_id: requestId },
      });
      if (!result.data)
        throw new Error("アプリへ接続できませんでした。もう一度Passkeyでログインしてください。");
      const url = new URL(result.data.callback_url);
      if (url.protocol !== "oyakoflow:" || url.hostname !== "auth")
        throw new Error("接続先を確認できませんでした。");
      window.location.href = url.href;
    } catch (e) {
      setError(e instanceof Error ? e.message : "接続できませんでした。");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-shell">
      <section className="auth-card">
        <p>Oyako Flow</p>
        <h1>
          {requiresPasskey ? "Passkeyを追加" : verifying ? "保護者の本人確認" : "親用アプリに接続"}
        </h1>
        {requiresPasskey ? (
          <>
            <p>
              機種変更や復旧後もログインできるよう、この保護者のアカウントにPasskeyを追加します。
            </p>
            {!added ? (
              <>
                <label>
                  Passkeyの名前
                  <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} />
                </label>
                <button disabled={busy || !label.trim()} onClick={() => void addPasskey()}>
                  Passkeyを登録する
                </button>
              </>
            ) : (
              <p role="status">Passkeyを登録しました。アプリに戻れます。</p>
            )}
          </>
        ) : (
          <p>
            {verifying
              ? "同じ保護者のPasskeyで本人確認し、設定の操作を続けます。"
              : "このiPhone・iPadで今日の予定を確認・変更できるようにします。"}
          </p>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button disabled={busy || (requiresPasskey && !added)} onClick={() => void approve()}>
          {busy
            ? "処理しています…"
            : verifying || requiresPasskey
              ? "アプリに戻る"
              : "このアプリに接続する"}
        </button>
        <button disabled={busy} onClick={onLogout}>
          別のPasskeyでログインする
        </button>
      </section>
    </main>
  );
}
